import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import type { StoryFormat } from "Components/Stories/Management/storyFormat";
import { STORY_DURATION_MS } from "Components/Stories/StoryProgressBars";
import {
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
  MAX_VIDEO_DURATION_SECONDS,
  formatAcceptedTypes,
} from "Components/Stories/storyMediaLimits";
import type { ReactNode } from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Stories.PostStory.Form.Format;

type Props = {
  value: StoryFormat;
  onChange: (format: StoryFormat) => void;
};

const StyledGroup = styled(RadioGroup)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    grid-template-columns: 1fr;
  }

  .MuiFormControlLabel-root {
    margin: 0;
    min-width: 0;
  }

  .MuiFormControlLabel-label {
    width: 100%;
  }

  .MuiRadio-root:focus-visible + .MuiFormControlLabel-label > div,
  .MuiRadio-root:has(input:focus-visible) + .MuiFormControlLabel-label > div {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 2px;
  }
`;

const StyledCard = styled.div<{ $isSelected: boolean }>`
  display: flex;
  position: relative;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;
  border: ${({ theme, $isSelected }) =>
    $isSelected
      ? `2px solid ${theme.palette.primary.main}`
      : `1px solid ${theme.colors.border}`};
  /* Keep the card the same size whether the border is 1px or 2px. */
  margin: ${({ $isSelected }) => ($isSelected ? 0 : "1px")};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? `${theme.palette.primary.main}0d`
      : theme.palette.common.white};

  &:hover {
    background-color: ${({ theme, $isSelected }) =>
      $isSelected ? `${theme.palette.primary.main}14` : theme.colors.gray};
  }
`;

const StyledIconBox = styled.div<{ $isSelected: boolean }>`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius};
  color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.palette.primary.main : theme.colors.gray};
`;

/* Off-screen rather than display:none, so the radio group keeps its keyboard and screen-reader behaviour. */
const visuallyHiddenRadioStyles = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
} as const;

type FormatCardProps = {
  format: StoryFormat;
  isSelected: boolean;
  icon: ReactNode;
  title: string;
  hint: string;
};

const FormatCard = ({
  format,
  isSelected,
  icon,
  title,
  hint,
}: FormatCardProps) => (
  <FormControlLabel
    value={format}
    control={<Radio sx={visuallyHiddenRadioStyles} checked={isSelected} />}
    label={
      <StyledCard $isSelected={isSelected}>
        <StyledIconBox $isSelected={isSelected}>{icon}</StyledIconBox>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        </div>
        {isSelected ? (
          <RadioButtonCheckedIcon color="primary" />
        ) : (
          <RadioButtonUncheckedIcon color="action" />
        )}
      </StyledCard>
    }
  />
);

/** Two-card radio choosing whether the story is a photo or a video; the form owns the value. */
export const StoryFormatPicker = ({ value, onChange }: Props) => {
  const { t } = useResource();

  return (
    <StyledGroup
      value={value}
      onChange={(_, next) => onChange(next === "video" ? "video" : "photo")}
    >
      <FormatCard
        format="photo"
        isSelected={value === "photo"}
        icon={<ImageOutlinedIcon />}
        title={t(RESOURCES.PhotoTitle)}
        hint={t(RESOURCES.PhotoHint, {
          formats: formatAcceptedTypes(ACCEPTED_IMAGE_TYPES),
          seconds: STORY_DURATION_MS / 1000,
        })}
      />
      <FormatCard
        format="video"
        isSelected={value === "video"}
        icon={<VideocamOutlinedIcon />}
        title={t(RESOURCES.VideoTitle)}
        hint={t(RESOURCES.VideoHint, {
          formats: formatAcceptedTypes(ACCEPTED_VIDEO_TYPES),
          seconds: MAX_VIDEO_DURATION_SECONDS,
        })}
      />
    </StyledGroup>
  );
};
